import { LockKeyhole, LogIn } from "lucide-react";
import { FormEvent, useState } from "react";
import { clearSession, fetchSessionProfile, login, persistSession, type Session } from "../lib/api";
import type { SupportedLocale } from "../lib/preferences";
import { LanguageMenu } from "./LanguageMenu";
import { Button } from "./ui/button";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

type LoginViewProps = {
  locale: SupportedLocale;
  onLocaleChange: (locale: SupportedLocale) => Promise<void>;
  onSession: (session: Session) => void;
  onBack?: () => void;
};

export function LoginView({ locale, onLocaleChange, onSession, onBack }: LoginViewProps) {
  const { t } = useTranslation("common");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setPending(true);
    try {
      const session = await login(email, password);
      const profile = await fetchSessionProfile();
      const enriched = persistSession({
        ...session,
        actorRole: profile.actor_role,
        workspace: profile.default_workspace,
        userID: profile.subject
      });
      onSession(enriched);
    } catch {
      clearSession();
      setError(t("auth.failed"));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-labelledby="auth-title">
        <div className="auth-copy">
          <div className="auth-mark">
            <LockKeyhole aria-hidden="true" size={22} />
          </div>
          <span className="eyebrow">LeadStack</span>
          <h1 id="auth-title">{t("auth.title")}</h1>
          <p>{t("auth.subtitle")}</p>
          <div className="auth-points">
            <span>{t("nav.admin")}</span>
            <span>{t("nav.advertiser")}</span>
            <span>{t("nav.webmaster")}</span>
            <span>{t("nav.offers")}</span>
            <span>{t("nav.flows")}</span>
          </div>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-topline">
            <div className="auth-titleline">
              <LogIn aria-hidden="true" size={18} />
              <strong>{t("auth.submit")}</strong>
            </div>
            <LanguageMenu onChange={onLocaleChange} value={locale} />
          </div>

          {onBack ? (
            <Button className="auth-back" icon={<ArrowLeft aria-hidden="true" size={16} />} onClick={onBack} size="sm" variant="ghost">
              {t("landing.actions.backToLanding")}
            </Button>
          ) : null}

          <label className="form-field">
            <span>{t("auth.email")}</span>
            <input autoComplete="username" onChange={(event) => setEmail(event.target.value)} required type="email" value={email} />
          </label>
          <label className="form-field">
            <span>{t("auth.password")}</span>
            <input
              autoComplete="current-password"
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <Button data-testid="login-submit" disabled={pending} icon={<LogIn aria-hidden="true" size={16} />} type="submit" variant="primary">
            {t("auth.submit")}
          </Button>
          <p className="auth-hint">{t("auth.hint")}</p>
        </form>
      </section>
    </main>
  );
}
